import { CalendarDays, Copy, Download, ExternalLink, FileText, MapPin, Share2, UserRound, Zap } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, eventFileUrl } from "@/services/eventsApi";
import { track } from "@/services/metrics";

const formatDate = (value) => new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value));
const formatTime = (value) => new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));

const timeBadge = (event) => {
  if (event.status === "ongoing") return "Live now";
  if (event.status !== "upcoming") return null;
  const days = Math.ceil((new Date(event.start_date) - Date.now()) / 86400000);
  if (days < 0) return null;
  if (days === 0) return "Starting today";
  return days === 1 ? "Starts tomorrow" : `Starts in ${days} days`;
};

export const EventCard = ({ event, index }) => {
  const testId = `event-card-${index + 1}`;
  const badge = timeBadge(event);
  const shareUrl = `${BACKEND_URL}/api/share/events/${event.event_id}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Event link copied — paste it anywhere, including Instagram bio or stories."); }
    catch { window.prompt("Copy this event link:", shareUrl); }
  };
  return (
    <article className="event-card" id={event.event_id} style={{ "--delay": `${Math.min(index, 5) * 110}ms` }} data-testid={testId}>
      <div className="event-card-media">
        {event.cover_url ? <img src={eventFileUrl(event.cover_url)} alt={`${event.title} cover`} loading="lazy" data-testid={`${testId}-cover`} /> : <div className="event-cover-fallback" data-testid={`${testId}-cover-fallback`}><span>MM</span><p>Event update</p></div>}
        <span className={`event-status is-${event.status}`} data-testid={`${testId}-status`}>{event.status}</span>
        {badge && <span className={`event-time-chip${event.status === "ongoing" ? " is-live" : ""}`} data-testid={`${testId}-time-chip`}>{event.status === "ongoing" && <Zap/>}{badge}</span>}
      </div>
      <div className="event-card-content">
        <span className="event-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
        <p className="eyebrow" data-testid={`${testId}-eyebrow`}>MADMINUTE / Event {String(index + 1).padStart(2, "0")}</p>
        <h2 data-testid={`${testId}-title`}>{event.title}</h2>
        <div className="event-facts">
          <span data-testid={`${testId}-date`}><CalendarDays/> {formatDate(event.start_date)}{event.end_date ? ` — ${formatDate(event.end_date)}` : ` · ${formatTime(event.start_date)}`}</span>
          <span data-testid={`${testId}-location`}><MapPin/> {event.location}</span>
          {event.organizer && <span data-testid={`${testId}-organizer`}><UserRound/> {event.organizer}</span>}
        </div>
        <p className="event-summary" data-testid={`${testId}-summary`}>{event.summary}</p>
        <p className="event-details" data-testid={`${testId}-details`}>{event.details}</p>
        {event.contact && <p className="event-contact" data-testid={`${testId}-contact`}>Contact: {event.contact}</p>}
        <div className="event-actions">
          {event.brochure_url && <><a href={eventFileUrl(event.brochure_url)} target="_blank" rel="noreferrer" className="button button-outline" onClick={() => track("brochure_view", "/events")} data-testid={`${testId}-view-brochure`}><FileText/> View brochure</a><a href={`${eventFileUrl(event.brochure_url)}?download=true`} className="event-download" onClick={() => track("brochure_view", "/events")} data-testid={`${testId}-download-brochure`}><Download/> Download PDF</a></>}
          {event.registration_url && <a href={event.registration_url} target="_blank" rel="noreferrer" className="button button-primary" data-testid={`${testId}-registration`}>Event details <ExternalLink/></a>}
        </div>
        <div className="event-share-row" data-testid={`${testId}-share-row`}>
          <span><Share2/> Share</span>
          <a href={`https://wa.me/?text=${encodeURIComponent(`${event.title} — MADMINUTE event\n${shareUrl}`)}`} target="_blank" rel="noreferrer" data-testid={`${testId}-share-whatsapp`}>WhatsApp</a>
          <button onClick={copyLink} data-testid={`${testId}-copy-link`}><Copy/> Copy link</button>
        </div>
      </div>
    </article>
  );
};
